import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ClipboardPen } from 'lucide-react';
import Loader from '@/components/Loader';
import type { GetDdiSearchResultResponse } from '@/types/dueDiligence.types';

const formatAuditTimestamp = (iso: string) => format(new Date(iso), 'MM/dd/yy hh:mm a');

type Props = {
  searchResult: GetDdiSearchResultResponse | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

export const SearchResultsDdiContent = ({ searchResult, isLoading, isError, onRetry }: Props) => {
  const navigate = useNavigate();

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-6 py-10 text-sm text-placeholder">
        <span>FAILED TO LOAD SEARCH RESULTS</span>
        <button type="button" className="text-primary text-sm underline" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <Loader isLoading={isLoading}>
      {!searchResult ? (
        <div className="w-full justify-center flex font-medium text-sm text-placeholder px-6 py-6">
          No search result found
        </div>
      ) : (
        <SearchResultCard
          searchResult={searchResult}
          onNavigate={() => navigate(`/due-diligence/${searchResult.workOrderId}/search-results/view`)}
        />
      )}
    </Loader>
  );
};

const SearchResultCard = ({
  searchResult,
  onNavigate,
}: {
  searchResult: GetDdiSearchResultResponse;
  onNavigate: () => void;
}) => {
  return (
    <div className="w-full px-6 py-6">
      <div
        role="button"
        tabIndex={0}
        className="flex flex-row items-start gap-3 p-4 rounded-lg bg-[#F5F5F5] cursor-pointer"
        onClick={onNavigate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onNavigate();
          }
        }}
      >
        <ClipboardPen className="w-6 h-6 text-primary flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <span className="font-medium text-sm text-primary">Search Results</span>
          <span className="text-xs text-placeholder">
            Added: {formatAuditTimestamp(searchResult.createdOn)} by {searchResult.createdBy.name}
          </span>
          {searchResult.updatedOn && searchResult.updatedBy && (
            <span className="text-xs text-placeholder">
              Updated: {formatAuditTimestamp(searchResult.updatedOn)} by {searchResult.updatedBy.name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsDdiContent;
